import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { useAuth } from "../context/AuthContext";

export default function CreatorProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("articles");
  const [following, setFollowing] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/creators/${username}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [username]);

  if (p === false) return <div className="pti-container py-24 text-center">Creator not found.</div>;
  if (!p) return <div className="pti-container py-24 text-center text-gray-400">Loading…</div>;

  const follow = async () => {
    if (!user) return toast.error("Sign in to follow creators.");
    const { data } = await api.post(`/creators/${username}/follow`); setFollowing(data.following);
    toast.success(data.following ? `Following ${p.display_name}` : "Unfollowed");
  };

  const M = p.metrics;
  return (
    <div data-testid="creator-profile">
      <div className="h-48 sm:h-64 bg-gray-100 overflow-hidden">{p.cover && <img src={p.cover} alt="" className="w-full h-full object-cover" />}</div>
      <div className="pti-container">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
          <img src={p.avatar || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=300"} alt={p.display_name} className="w-28 h-28 rounded-full object-cover border-4 border-white bg-gray-100" />
          <div className="flex-1 pb-2">
            <h1 className="font-display text-3xl font-black flex items-center gap-2">{p.display_name}{p.verified && <CheckCircle2 size={22} className="text-pti-red" aria-label="Verified creator" />}</h1>
            <div className="text-gray-500">@{p.username}</div>
          </div>
          <button onClick={follow} data-testid="profile-follow" className={`px-6 py-2.5 font-bold border transition-colors ${following ? "bg-white text-pti-black border-pti-black" : "bg-pti-red text-white border-pti-red hover:bg-pti-redhover"}`}>{following ? "Following" : "Follow"}</button>
        </div>
        <p className="text-gray-700 mt-4 max-w-2xl">{p.bio}</p>

        <div className="grid grid-cols-4 gap-4 mt-6 max-w-lg">
          {[["Views", M.views], ["Reads", M.reads], ["Likes", M.likes], ["Followers", M.followers]].map(([l, v]) => (
            <div key={l} className="text-center border border-gray-200 py-3">
              <div className="font-display text-2xl font-bold">{v}</div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500">{l}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-6 mt-8 border-b border-gray-200" role="tablist">
          {["articles", "shorts"].map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`profile-tab-${t}`}
              className={`text-sm font-bold uppercase tracking-wider pb-2 border-b-2 transition-colors ${tab === t ? "border-pti-red text-pti-black" : "border-transparent text-gray-500"}`}>
              {t} ({t === "articles" ? p.articles.length : p.shorts.length})
            </button>
          ))}
        </div>

        {tab === "articles" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-8 pb-10">
            {p.articles.map((a) => <ArticleCard key={a.id} article={{ ...a, author: { username: p.username, display_name: p.display_name, verified: p.verified, avatar: p.avatar } }} />)}
            {p.articles.length === 0 && <p className="text-gray-400">No published articles yet.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 pb-10">
            {p.shorts.map((s) => (
              <div key={s.id} className="relative aspect-[9/16] overflow-hidden bg-black">
                <img src={s.thumbnail} alt={s.title} className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <p className="absolute bottom-2 left-2 right-2 text-white text-xs font-semibold line-clamp-2">{s.caption}</p>
              </div>
            ))}
            {p.shorts.length === 0 && <p className="text-gray-400">No published Shorts yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
